import type { MapboxSettings, MapboxStylePreset, MapboxThemeSlot } from "./types";

export const MAPBOX_STYLE_PRESETS: Record<MapboxStylePreset, string> = {
  streets: "mapbox://styles/mapbox/streets-v12",
  light: "mapbox://styles/mapbox/light-v11",
  dark: "mapbox://styles/mapbox/dark-v11",
};

/** Labels-on satellite. Map toolbar toggle — not a Settings theme. */
export const MAPBOX_SATELLITE_STYLE = "mapbox://styles/mapbox/satellite-streets-v12";

export const MAPBOX_STYLE_PRESET_IDS: MapboxStylePreset[] = ["streets", "light", "dark"];

export const DEFAULT_MAPBOX: MapboxSettings = {
  accessToken: "",
  dayPreset: "light",
  nightPreset: "dark",
  styleUrlDay: "",
  styleUrlNight: "",
};

export const MAPBOX_TOKEN_MISSING_HINT =
  "Set a Mapbox public token (pk.…) under Settings → Desk → Dispatcher Settings to load the flight map. Tiles stay empty until then — nothing is invented.";

const PUBLIC_TOKEN = /^pk\.[A-Za-z0-9._-]+$/;
const SECRET_TOKEN = /^sk\./i;

/** Older single-preset / single-URL saves, plus dayStyleUrl / nightStyleUrl aliases. */
export type LegacyMapboxSettings = {
  stylePreset?: unknown;
  styleUrl?: unknown;
  dayPreset?: unknown;
  nightPreset?: unknown;
  styleUrlDay?: unknown;
  styleUrlNight?: unknown;
  dayStyleUrl?: unknown;
  nightStyleUrl?: unknown;
  accessToken?: unknown;
};

export const MAP_STYLE_SLOT_KEY = "ns.dispatch.map.styleSlot";
/** Map toolbar Satellite overlay. Local desk pref — not a Settings / company theme. */
export const MAP_SATELLITE_KEY = "ns.dispatch.map.satellite";

export type MapBasemapPref = {
  /** Null = first open follows desk day/night. */
  styleSlot: MapboxThemeSlot | null;
  satellite: boolean;
};

export type MapboxStyleChrome = {
  theme?: unknown;
  themePack?: unknown;
  /** Map toolbar Satellite overlay. Persisted locally; not a Settings theme. */
  satellite?: unknown;
  /**
   * Explicit Light/Dark map slot. Independent of desk day/night and the 5 app themes.
   * `day` / `light` → day URL. `night` / `dark` → night URL.
   */
  styleSlot?: unknown;
};

export function hydrateMapboxStylePreset(raw: unknown): MapboxStylePreset | undefined {
  return raw === "streets" || raw === "light" || raw === "dark" ? raw : undefined;
}

function trimUrl(raw: unknown): string {
  return typeof raw === "string" ? raw.trim() : "";
}

function isCustomStyleUrl(value: string): boolean {
  return value.startsWith("mapbox://") || /^https:\/\//i.test(value);
}

function pickStyleUrl(primary: unknown, alias: unknown): string {
  const first = trimUrl(primary);
  return first || trimUrl(alias);
}

export function hydrateMapStyleSlot(raw: unknown): MapboxThemeSlot | undefined {
  if (raw === "day" || raw === "light") return "day";
  if (raw === "night" || raw === "dark") return "night";
  return undefined;
}

export function readMapStyleSlot(storage: Pick<Storage, "getItem"> | null): MapboxThemeSlot | null {
  return hydrateMapStyleSlot(storage?.getItem(MAP_STYLE_SLOT_KEY)) ?? null;
}

export function writeMapStyleSlot(
  slot: MapboxThemeSlot,
  storage: Pick<Storage, "setItem"> | null,
): MapboxThemeSlot {
  storage?.setItem(MAP_STYLE_SLOT_KEY, slot);
  return slot;
}

export function readMapSatellite(storage: Pick<Storage, "getItem"> | null): boolean {
  return storage?.getItem(MAP_SATELLITE_KEY) === "1";
}

export function writeMapSatellite(on: boolean, storage: Pick<Storage, "setItem"> | null): boolean {
  storage?.setItem(MAP_SATELLITE_KEY, on ? "1" : "0");
  return on;
}

export function readMapBasemapPref(storage: Pick<Storage, "getItem"> | null): MapBasemapPref {
  return {
    styleSlot: readMapStyleSlot(storage),
    satellite: readMapSatellite(storage),
  };
}

/**
 * Map Light/Dark override wins. Else light/day desk theme → day slot.
 * Dark theme or Ops Night pack → night slot. Unknown chrome falls through to night.
 */
export function mapboxThemeSlot(chrome?: MapboxStyleChrome | null): MapboxThemeSlot {
  const override = hydrateMapStyleSlot(chrome?.styleSlot);
  if (override) return override;
  if (chrome?.themePack === "ops-night") return "night";
  if (chrome?.theme === "light") return "day";
  return "night";
}

export function hydrateMapboxSettings(raw?: Partial<MapboxSettings> | LegacyMapboxSettings | null): MapboxSettings {
  const record = raw && typeof raw === "object" ? (raw as LegacyMapboxSettings) : null;
  const legacyPreset = hydrateMapboxStylePreset(record?.stylePreset);
  const legacyWasSatellite = record?.stylePreset === "satellite";
  const legacyUrl = trimUrl(record?.styleUrl);
  const hasDayUrl = record != null && ("styleUrlDay" in record || "dayStyleUrl" in record);
  const hasNightUrl = record != null && ("styleUrlNight" in record || "nightStyleUrl" in record);
  const forcedLegacyPreset = legacyPreset && legacyPreset !== "dark" ? legacyPreset : undefined;

  return {
    accessToken: trimUrl(record?.accessToken),
    dayPreset: hydrateMapboxStylePreset(record?.dayPreset) ?? forcedLegacyPreset ?? DEFAULT_MAPBOX.dayPreset,
    nightPreset: hydrateMapboxStylePreset(record?.nightPreset) ?? (legacyWasSatellite ? undefined : legacyPreset) ?? DEFAULT_MAPBOX.nightPreset,
    styleUrlDay: hasDayUrl ? pickStyleUrl(record?.styleUrlDay, record?.dayStyleUrl) : legacyUrl,
    styleUrlNight: hasNightUrl ? pickStyleUrl(record?.styleUrlNight, record?.nightStyleUrl) : legacyUrl,
  };
}

/** Valid public token for Mapbox GL JS. Incomplete paste or `sk.` → empty (fail soft). */
export function mapboxAccessToken(settings: Pick<MapboxSettings, "accessToken"> | null | undefined): string {
  const token = settings?.accessToken?.trim() ?? "";
  return PUBLIC_TOKEN.test(token) ? token : "";
}

export function isMapboxPublicToken(token: string): boolean {
  return PUBLIC_TOKEN.test(token.trim());
}

export function isMapboxSecretToken(token: string): boolean {
  return SECRET_TOKEN.test(token.trim());
}

/**
 * Light/Dark map slot (override or desk-theme default) picks day vs night URL.
 * Custom URL for that slot wins when it is a mapbox:// or https:// style.
 * `satellite: true` is the map toolbar overlay (stock satellite-streets).
 */
export function resolveMapboxStyle(
  settings?: Partial<MapboxSettings> | LegacyMapboxSettings | null,
  chrome?: MapboxStyleChrome | null,
): string {
  if (chrome?.satellite === true) return MAPBOX_SATELLITE_STYLE;
  const hydrated = hydrateMapboxSettings(settings);
  const slot = mapboxThemeSlot(chrome);
  const custom = slot === "day" ? hydrated.styleUrlDay : hydrated.styleUrlNight;
  if (isCustomStyleUrl(custom)) return custom;
  const preset = slot === "day" ? hydrated.dayPreset : hydrated.nightPreset;
  return MAPBOX_STYLE_PRESETS[preset];
}

/**
 * Snapshot / Nanode publish: keep a public `pk.` token so Mobile can load the same map.
 * Secret `sk.` tokens and junk never leave the desk.
 */
export function publishMapboxSettings(settings?: Partial<MapboxSettings> | LegacyMapboxSettings | null): MapboxSettings {
  const hydrated = hydrateMapboxSettings(settings);
  return {
    ...hydrated,
    accessToken: isMapboxPublicToken(hydrated.accessToken) ? hydrated.accessToken : "",
  };
}

export function mapboxStyleKey(
  settings?: Partial<MapboxSettings> | LegacyMapboxSettings | null,
  chrome?: MapboxStyleChrome | null,
): string {
  return `${mapboxAccessToken(hydrateMapboxSettings(settings))}|${resolveMapboxStyle(settings, chrome)}`;
}
